import { useMemo, useEffect, useState } from 'react';
import { useAppDisplayModeContext } from '@archipro-design/aria';
import {
    useLoaderData,
    useNavigation,
    useSearchParams,
} from '@remix-run/react';
import { useTracker, type TrackingEvent } from '@archipro-website/tracker';
import { useSetAtom } from 'jotai';
import { uiStateAtomReachedProjectPageContent } from '@rocco/ui/website-footer/states/ui-states-reached-project-page-content';
import type { TileViewModel } from '@rocco/ui/tile-shared/view-models/tile';
import type { ProductTileViewModel } from '@rocco/ui/tile-product/view-models/product';
import type { ProfessionalTileViewModel } from '@rocco/ui/tile-professional/view-models/professional';
import {
    type ProjectTileViewModel,
    isProjectTileViewModel,
} from '@rocco/ui/tile-project/view-models/project';
import { PermissionEnum } from 'generated/graphql';
import type { loader } from '~/routes/_app.project.$urlSegment._index';
import { getBMPagePath } from '~/modules/root/util/get-bm-page-path';
import { useEditPermissions, usePermissions } from '~/modules/user';
import AboutProfessional from '@modules/project/component/about-professional/AboutProfessional';
import { DetailPageButtons } from '@modules/business-manager/component/detail-page-buttons/DetailPageButtons';
import { useSetInLibrary } from '~/modules/directory/hook/use-set-in-library';
import { useAppData } from '~/modules/root/hook/use-app-data';
import { useToaster } from '~/modules/root/context/ToasterContext';
import { DetailsPageRelatedTileGrid } from '~/ui-rocco/pages/details-page/DetailsPageRelatedTileGrid';
import { CarouselItem, type CarouselOptions } from '@rocco/components/carousel';
import { Tile } from '@rocco/ui/tile-shared/components/Tile';
import { useTilesAdminData } from '~/modules-rocco/admin/hooks/use-tiles-admin-data';
import { CarouselSection } from '~/ui-rocco/components/carousel-section/CarouselSection';
import { useHandleFollow } from '~/modules-rocco/design-board/hook/use-handle-follow';
import { PROFESSIONAL_SECTIONS_ANCHOR } from '~/modules/professional/util/constants';
import { ProjectTeam } from '../details-accordion/ProjectTeam';
import { useEnquiryModal } from '~/modules/enquiry/hooks/use-enquiry-modal';
import { useRecentlyViewed } from '~/modules-rocco/recently-viewed/hooks/use-recently-viewed';
import { TileOverlayWithFollowSelector } from '~/modules/moodboards/components';
import { useEnquiryVisibility } from '~/modules/enquiry/hooks/use-enquiry-visibility';

const SHOW_ALL_PARAM_FEATURED_PRODUCTS = 'featured-products-show-all';
export const SHOW_ALL_PARAM_FEATURED_PROFESSIONALS =
    'featured-professionals-show-all';
const SHOW_ALL_PARAM_POPULAR_PROJECTS = 'popular-projects-show-all';
const SHOW_ALL_PARAM_RECENTLY_VIEWED_PROJECTS =
    'recently-viewed-projects-show-all';

const ProjectPageContent = () => {
    const loaderData = useLoaderData<typeof loader>();
    const { desktop } = useAppDisplayModeContext();
    const {
        project,
        popularProjects,
        featuredProducts,
        featuredProfessionals,
        featuredProjects,
        projectTeamMembers,
    } = loaderData;
    const { isInDesignBoard } = useHandleFollow();
    const tracker = useTracker();
    const enableCarousel = false;
    const { recentlyViewed: recentlyViewedTiles } = useRecentlyViewed();

    // Handler for tracking clicks on popular projects in "Other Projects you might like" section
    const handleOtherProjectLikeProjectClick = (libraryItemID: number) => {
        tracker.log('OtherProjectLikeProjectClick' as TrackingEvent, {
            url: new URL(window.location.href),
            data: {
                LibraryItemID: libraryItemID,
            },
        });
    };

    // Handler for tracking clicks on recently viewed projects
    const handleRecentlyViewedProjectClick = (libraryItemID: number) => {
        tracker.log('RecentlyViewedProjectClick' as TrackingEvent, {
            url: new URL(window.location.href),
            data: {
                LibraryItemID: libraryItemID,
            },
        });
    };

    // Deduplicate popular projects to exclude the current project and all projects by this professional
    const deduplicatedPopularProjects = useMemo(() => {
        return popularProjects.projects.filter(
            (popularProject) =>
                popularProject.professional.id !== project.Professional.ID,
        );
    }, [popularProjects.projects, project.Professional.ID]);

    const carouselOptionsProjects: CarouselOptions = useMemo(
        () => ({
            slidesToScroll: 4,
            align: 'start',
            dragFree: true,
            watchDrag: (api, evt) => {
                return (
                    evt.target instanceof Element &&
                    !evt.target.closest('.slider-item-image')
                );
            },
        }),
        [],
    );

    const carouselOptionsProducts: CarouselOptions = useMemo(
        () => ({
            slidesToScroll: 5,
            align: 'start',
            dragFree: true,
            watchDrag: (api, evt) => {
                return (
                    evt.target instanceof Element &&
                    !evt.target.closest('.slider-item-image')
                );
            },
        }),
        [],
    );

    const navigation = useNavigation();
    const [searchParams] = useSearchParams();
    const { canEditProjects } = useEditPermissions(project.Professional.ID);
    const canCopyLink = usePermissions(PermissionEnum.BmMarketingView);
    const projectEditPath = getBMPagePath(
        project.Professional.URLSegment,
        'projects',
        project.URLSegment,
    );

    const { user } = useAppData();
    const { toast } = useToaster();
    const isAdminUser =
        user.__typename === 'Me' &&
        (user.IsStaffAdmin === true ||
            ('IsHighAdmin' in user && user.IsHighAdmin === true));
    const [optimisticInLibrary, setOptimisticInLibrary] = useState(
        !!project.InLibrary,
    );
    useEffect(() => {
        setOptimisticInLibrary(!!project.InLibrary);
    }, [project.InLibrary]);
    const { setInLibrary } = useSetInLibrary(
        (error) => {
            setOptimisticInLibrary(!!project.InLibrary);
            toast(error.Message || 'Something went wrong', { type: 'error' });
        },
        (_, __, inLibrary) =>
            toast(
                inLibrary ? 'Added to directory.' : 'Removed from directory.',
                { type: 'success' },
            ),
    );

    const nextSearchParams = new URLSearchParams(navigation.location?.search);
    // Check URL params to determine if showing all items
    const showingAllFeaturedProducts = !desktop
        ? !!searchParams.get(SHOW_ALL_PARAM_FEATURED_PRODUCTS)
        : true;
    const showingAllFeaturedProfessionals = !desktop
        ? !!searchParams.get(SHOW_ALL_PARAM_FEATURED_PROFESSIONALS)
        : true;
    const showingAllPopularProjects = !!searchParams.get(
        SHOW_ALL_PARAM_POPULAR_PROJECTS,
    );
    const showingAllRecentlyViewedProjects = !!searchParams.get(
        SHOW_ALL_PARAM_RECENTLY_VIEWED_PROJECTS,
    );
    const featuredShowAllFeaturedProducts =
        nextSearchParams.get(SHOW_ALL_PARAM_FEATURED_PRODUCTS) === '1';
    const featuredShowAllFeaturedProfessionals =
        nextSearchParams.get(SHOW_ALL_PARAM_FEATURED_PROFESSIONALS) === '1';
    const featuredShowAllPopularProjects =
        nextSearchParams.get(SHOW_ALL_PARAM_POPULAR_PROJECTS) === '1';
    const featuredShowAllRecentlyViewedProjects =
        nextSearchParams.get(SHOW_ALL_PARAM_RECENTLY_VIEWED_PROJECTS) === '1';

    const isLoadingFeaturedProducts =
        navigation.state === 'loading' && featuredShowAllFeaturedProducts;
    const isLoadingFeaturedProfessionals =
        navigation.state === 'loading' && featuredShowAllFeaturedProfessionals;
    const isLoadingPopularProjects =
        navigation.state === 'loading' && featuredShowAllPopularProjects;
    const isLoadingRecentlyViewedProjects =
        navigation.state === 'loading' && featuredShowAllRecentlyViewedProjects;

    const featuredProductsList: TileViewModel<ProductTileViewModel>[] = useMemo(
        () =>
            featuredProducts.FeaturedProducts.products.map((product) => ({
                model: product,
                aspectRatio: 'square',
                section: 'ProjectUsedProducts',
            })),
        [featuredProducts.FeaturedProducts.products],
    );
    const featuredProfessionalsList: TileViewModel<ProfessionalTileViewModel>[] =
        useMemo(
            () =>
                featuredProfessionals.FeaturedProfessionals.professionals.map(
                    (professional) => ({
                        model: professional,
                        aspectRatio: 'default',
                        section: 'ProjectUsedProfessionals',
                    }),
                ),
            [featuredProfessionals.FeaturedProfessionals.professionals],
        );

    const featuredProjectsList: TileViewModel<ProjectTileViewModel>[] = useMemo(
        () =>
            featuredProjects.FeaturedProjects.projects.map((project) => ({
                model: project,
                aspectRatio: 'default',
                section: 'ProjectUsedProjects',
            })),
        [featuredProjects.FeaturedProjects.projects],
    );
    const popularProjectsList: TileViewModel<ProjectTileViewModel>[] = useMemo(
        () =>
            deduplicatedPopularProjects.map((project) => ({
                model: project,
                aspectRatio: 'default',
                section: 'PopularProjects',
            })),
        [deduplicatedPopularProjects],
    );
    const featuredProductsAdmin = useTilesAdminData(featuredProductsList);
    const featuredProfessionalsAdmin = useTilesAdminData(
        featuredProfessionalsList,
    );
    const popularProjectsAdmin = useTilesAdminData(popularProjectsList);
    const featuredProjectsAdmin = useTilesAdminData(featuredProjectsList);

    const recentlyViewedProjectsList: TileViewModel<ProjectTileViewModel>[] =
        useMemo(() => {
            return recentlyViewedTiles
                .filter((item): item is ProjectTileViewModel =>
                    isProjectTileViewModel(item),
                )
                .filter((item) => item.id !== project.ID) // Exclude current project
                .map((item) => ({
                    model: item,
                    aspectRatio: 'default' as const,
                    section: 'RecentlyViewedProjects',
                }));
        }, [recentlyViewedTiles, project.ID]);

    const recentlyViewedProjectsAdmin = useTilesAdminData(
        recentlyViewedProjectsList,
    );

    const getDisplayCount = (
        totalCount: number,
        type: 'products' | 'professionals' | 'projects',
    ) => {
        if (type === 'products') {
            return totalCount < 10 ? 5 : 10;
        }
        if (type === 'professionals') {
            return totalCount < 8 ? 4 : 8;
        }
        if (type === 'projects') {
            return totalCount < 8 ? 4 : 8;
        }
        return 0;
    };

    const displayedFeaturedProducts = getDisplayCount(
        featuredProducts.FeaturedProducts.pageInfo.totalCount,
        'products',
    );
    const displayedFeaturedProfessionals = getDisplayCount(
        featuredProfessionals.FeaturedProfessionals.pageInfo.totalCount,
        'professionals',
    );
    const displayedPopularProjects = getDisplayCount(
        popularProjects.projects.length,
        'projects',
    );
    const displayedRecentlyViewedProjects = getDisplayCount(
        recentlyViewedProjectsList.length,
        'projects',
    );

    // Let the FloatingIsland observe the ProjectPageContent container and update the atom
    const setReachedProjectPageContent = useSetAtom(
        uiStateAtomReachedProjectPageContent,
    );

    // Hide the island when the user scrolls past the page builder contents and reaches the ProjectPageContent container.
    useEffect(() => {
        const el = document.getElementById('project-page-content-root');
        if (!el) return;

        let prevScrollY = window.scrollY;
        const viewportBottom = window.innerHeight;

        const onScroll = () => {
            const currentScrollY = window.scrollY;
            const scrollingDown = currentScrollY > prevScrollY;
            prevScrollY = currentScrollY;

            const rect = el.getBoundingClientRect();
            const top = rect.top;

            if (scrollingDown) {
                // hide island as soon as the container's top enters the viewport from the bottom
                if (top <= viewportBottom) {
                    setReachedProjectPageContent(true);
                }
            } else {
                // show island when we scroll back up
                if (top > viewportBottom) {
                    setReachedProjectPageContent(false);
                }
            }
        };

        window.addEventListener('scroll', onScroll, { passive: true });

        onScroll();

        return () => window.removeEventListener('scroll', onScroll);
    }, [setReachedProjectPageContent]);

    // Reset the project page content atom when component unmounts (navigating away from project page)
    useEffect(() => {
        return () => {
            setReachedProjectPageContent(false);
        };
    }, [setReachedProjectPageContent]);

    // Get suppliers from featured professionals
    // Since isManufacturer is not available in tile data, we'll use category or services to identify suppliers
    const suppliers = useMemo(() => {
        const supplierProfessionals =
            featuredProfessionals.FeaturedProfessionals.professionals.filter(
                (professional) => {
                    // Check if category or services indicate this is a supplier/manufacturer
                    const categoryLower = professional.category.toLowerCase();
                    const servicesLower = professional.services.map((s) =>
                        s.toLowerCase(),
                    );

                    return (
                        categoryLower.includes('supplier') ||
                        categoryLower.includes('manufacturer') ||
                        categoryLower.includes('product') ||
                        servicesLower.some(
                            (service) =>
                                service.includes('supplier') ||
                                service.includes('manufacturer') ||
                                service.includes('product'),
                        )
                    );
                },
            );

        return supplierProfessionals;
    }, [featuredProfessionals]);

    const { openEnquiryModal } = useEnquiryModal({
        professional: project.Professional,
        itemType: 'professional',
        siteTreeID: project.ID,
        relatedItem: {
            id: project.ID,
            type: 'project',
            title: project.Title,
            category: project.categoryPath,
        },
    });
    const { showEnquiryCTA, showSpecifierLoginPrompt } = useEnquiryVisibility(
        project.Professional,
    );

    // Check if there are any public team members to display
    const hasTeamMembers = useMemo(() => {
        if (!projectTeamMembers || projectTeamMembers.length === 0) {
            return false;
        }

        // Check if any category has public team members
        return projectTeamMembers.some((category) => {
            return (
                category.TeamMembers &&
                category.TeamMembers.some((member) => member.IsPublic !== false)
            );
        });
    }, [projectTeamMembers]);

    return (
        <div
            id="project-page-content-root"
            className="flex flex-col gap-y-12 px-4 md:gap-y-8 md:px-0"
        >
            {desktop && hasTeamMembers && (
                <div className="md:px-12 md:py-8">
                    <ProjectTeam
                        projectID={project.ID}
                        openEnquiryModal={openEnquiryModal}
                        showEnquiryCTA={showEnquiryCTA}
                        showSpecifierLoginPrompt={showSpecifierLoginPrompt}
                        professionalName={project.Professional.Title}
                    />
                </div>
            )}
            {featuredProducts.FeaturedProducts.pageInfo.totalCount > 0 &&
                desktop &&
                (enableCarousel ? (
                    <CarouselSection
                        title="Products Featured in this Project"
                        options={carouselOptionsProducts}
                        className="py-20 md:px-12"
                    >
                        {featuredProductsAdmin.map((tile) => (
                            <CarouselItem
                                key={`featured-product-${tile.model.id}`}
                                className="max-w-30 first:ml-0 md:max-w-87"
                            >
                                <Tile
                                    tile={{
                                        ...tile,
                                        isFollowed: isInDesignBoard(
                                            tile.model.id,
                                            'ProductPin',
                                        ),
                                        overlay: (
                                            <TileOverlayWithFollowSelector
                                                pinId={tile.model.id}
                                                pinType="ProductPin"
                                            />
                                        ),
                                    }}
                                />
                            </CarouselItem>
                        ))}
                    </CarouselSection>
                ) : (
                    <div className="md:px-12 md:py-8">
                        <DetailsPageRelatedTileGrid
                            type="products"
                            title={`Products used`}
                            titleClassName="text-9 font-display font-semibold leading-[100%] tracking-[-0.4px]"
                            appDisplayMode={desktop ? 'desktop' : 'mobile'}
                            displayCount={{
                                desktop: displayedFeaturedProducts,
                                mobile: 4,
                            }}
                            noSlice={showingAllFeaturedProducts}
                            tiles={featuredProductsAdmin}
                            cta={
                                showingAllFeaturedProducts ||
                                featuredProducts.FeaturedProducts.pageInfo
                                    .totalCount <= displayedFeaturedProducts
                                    ? undefined
                                    : {
                                          type: 'link',
                                          to: `?${SHOW_ALL_PARAM_FEATURED_PRODUCTS}=1`,
                                          label: 'Show all Products used',
                                          loading: isLoadingFeaturedProducts
                                              ? true
                                              : undefined,
                                          preventScrollReset: true,
                                          color: 'primary',
                                      }
                            }
                        />
                    </div>
                ))}

            <section className="md:pl-12">
                {/* Featured Products */}
                {!desktop && (
                    <DetailsPageRelatedTileGrid
                        type="products"
                        title={`Products Used`}
                        tiles={featuredProductsAdmin}
                        cta={
                            showingAllFeaturedProducts ||
                            featuredProducts.FeaturedProducts.pageInfo
                                .totalCount <= 4
                                ? undefined
                                : {
                                      type: 'link',
                                      to: `?${SHOW_ALL_PARAM_FEATURED_PRODUCTS}=1`,
                                      label: 'View all',
                                      loading: isLoadingFeaturedProducts
                                          ? true
                                          : undefined,
                                      preventScrollReset: true,
                                  }
                        }
                        displayCount={{
                            desktop: 10,
                            mobile: 4,
                        }}
                        appDisplayMode={desktop ? 'desktop' : 'mobile'}
                        noSlice={showingAllFeaturedProducts}
                    />
                )}

                {/* Featured Professionals */}
                {!desktop && (
                    <DetailsPageRelatedTileGrid
                        type="professionals"
                        title={`Professionals used`}
                        tiles={featuredProfessionalsAdmin}
                        appDisplayMode={desktop ? 'desktop' : 'mobile'}
                        cta={
                            showingAllFeaturedProfessionals ||
                            featuredProfessionals.FeaturedProfessionals.pageInfo
                                .totalCount <= 4
                                ? undefined
                                : {
                                      type: 'link',
                                      to: `?${SHOW_ALL_PARAM_FEATURED_PROFESSIONALS}=1`,
                                      label: 'View all',
                                      loading: isLoadingFeaturedProfessionals
                                          ? true
                                          : undefined,
                                      preventScrollReset: true,
                                  }
                        }
                        displayCount={{
                            desktop: 4,
                            mobile: 4,
                        }}
                        noSlice={showingAllFeaturedProfessionals}
                    />
                )}
            </section>
            {featuredProfessionalsAdmin.length > 0 &&
                desktop &&
                (enableCarousel ? (
                    <CarouselSection
                        title={`Professionals and Brands used.`}
                        className="py-20 md:px-12"
                        options={carouselOptionsProjects}
                    >
                        {featuredProfessionalsAdmin.map((tile) => (
                            <CarouselItem
                                key={`featured-professional-${tile.model.id}`}
                                className="max-w-30 first:ml-0 md:max-w-110"
                            >
                                <Tile
                                    tile={{
                                        ...tile,
                                        isFollowed: isInDesignBoard(
                                            tile.model.id,
                                            'ProfessionalPin',
                                        ),
                                        overlay: (
                                            <TileOverlayWithFollowSelector
                                                pinId={tile.model.id}
                                                pinType="ProfessionalPin"
                                            />
                                        ),
                                    }}
                                />
                            </CarouselItem>
                        ))}
                    </CarouselSection>
                ) : (
                    <div className="md:px-12 md:py-8">
                        <DetailsPageRelatedTileGrid
                            type="professionals"
                            title={`Professionals used`}
                            titleClassName="mb-2 font-display text-20 font-semibold leading-[100%] text-[#3C3C3C]"
                            appDisplayMode={desktop ? 'desktop' : 'mobile'}
                            displayCount={{
                                desktop: displayedFeaturedProfessionals,
                                mobile: 0,
                            }}
                            noSlice={showingAllFeaturedProfessionals}
                            tiles={featuredProfessionalsAdmin}
                            hideBorder={suppliers.length > 0}
                            cta={
                                showingAllFeaturedProfessionals ||
                                featuredProfessionals.FeaturedProfessionals
                                    .pageInfo.totalCount <=
                                    displayedFeaturedProfessionals
                                    ? undefined
                                    : {
                                          type: 'link',
                                          to: `?${SHOW_ALL_PARAM_FEATURED_PROFESSIONALS}=1`,
                                          label: 'Show all Professionals used',
                                          loading:
                                              isLoadingFeaturedProfessionals
                                                  ? true
                                                  : undefined,
                                          preventScrollReset: true,
                                          color: 'primary',
                                      }
                            }
                        />
                    </div>
                ))}

            {desktop && (
                <section className="md:px-12">
                    <AboutProfessional
                        data={project}
                        tiles={featuredProjectsAdmin}
                        showEnquiryCTA={showEnquiryCTA}
                        showSpecifierLoginPrompt={showSpecifierLoginPrompt}
                    />
                </section>
            )}

            {/* Featured Projects */}
            {featuredProjectsAdmin.length > 0 && !desktop && (
                <DetailsPageRelatedTileGrid
                    type="projects"
                    title={`More projects by ${project.Professional.Title}`}
                    tiles={featuredProjectsAdmin}
                    appDisplayMode={desktop ? 'desktop' : 'mobile'}
                    cta={
                        featuredProjects.FeaturedProjects.pageInfo.totalCount <=
                        4
                            ? undefined
                            : {
                                  type: 'link',
                                  to: `${project.Professional.Link}/case-studies#${PROFESSIONAL_SECTIONS_ANCHOR}`,
                                  label: 'View more',
                              }
                    }
                    displayCount={{
                        desktop: 4,
                        mobile: 4,
                    }}
                />
            )}

            {/* Popular Projects Carousel*/}
            {deduplicatedPopularProjects.length > 0 &&
                desktop &&
                (enableCarousel ? (
                    <CarouselSection
                        title="Other People also viewed"
                        options={carouselOptionsProjects}
                        className="py-20 md:px-12"
                    >
                        {popularProjectsAdmin.map((tile) => (
                            <CarouselItem
                                key={`popular-project-${tile.model.id}`}
                                className="max-w-30 first:ml-0 md:max-w-110"
                            >
                                <div
                                    onClick={() =>
                                        handleOtherProjectLikeProjectClick(
                                            tile.model.id,
                                        )
                                    }
                                >
                                    <Tile
                                        tile={{
                                            ...tile,
                                            isFollowed: isInDesignBoard(
                                                tile.model.id,
                                                'ProjectPin',
                                            ),
                                            overlay: (
                                                <TileOverlayWithFollowSelector
                                                    pinId={tile.model.id}
                                                    pinType="ProjectPin"
                                                />
                                            ),
                                        }}
                                    />
                                </div>
                            </CarouselItem>
                        ))}
                    </CarouselSection>
                ) : (
                    <div className="md:px-12 md:py-8">
                        <DetailsPageRelatedTileGrid
                            type="projects"
                            title="Other People also viewed"
                            titleClassName="!text-15 font-display font-semibold leading-[100%] text-[#3C3C3C] mb-2"
                            tiles={popularProjectsAdmin}
                            displayCount={{
                                desktop: displayedPopularProjects,
                                mobile: 0,
                            }}
                            noSlice={showingAllPopularProjects}
                            appDisplayMode={desktop ? 'desktop' : 'mobile'}
                            cta={
                                showingAllPopularProjects ||
                                popularProjectsAdmin.length <=
                                    displayedPopularProjects
                                    ? undefined
                                    : {
                                          type: 'link',
                                          to: `?${SHOW_ALL_PARAM_POPULAR_PROJECTS}=1`,
                                          label: 'Show all Projects',
                                          preventScrollReset: true,
                                          loading: isLoadingPopularProjects
                                              ? true
                                              : undefined,
                                          color: 'primary',
                                      }
                            }
                        />
                    </div>
                ))}

            {/* Recently Viewed Projects */}
            {recentlyViewedProjectsList.length > 0 &&
                desktop &&
                (enableCarousel ? (
                    <CarouselSection
                        title="Recently viewed"
                        options={carouselOptionsProjects}
                        className="py-20 md:px-12"
                    >
                        {recentlyViewedProjectsAdmin.map((tile) => (
                            <CarouselItem
                                key={`recently-viewed-project-${tile.model.id}`}
                                className="max-w-30 first:ml-0 md:max-w-110"
                            >
                                <div
                                    onClick={() =>
                                        handleRecentlyViewedProjectClick(
                                            tile.model.id,
                                        )
                                    }
                                >
                                    <Tile
                                        tile={{
                                            ...tile,
                                            isFollowed: isInDesignBoard(
                                                tile.model.id,
                                                'ProjectPin',
                                            ),
                                            overlay: (
                                                <TileOverlayWithFollowSelector
                                                    pinId={tile.model.id}
                                                    pinType="ProjectPin"
                                                />
                                            ),
                                        }}
                                    />
                                </div>
                            </CarouselItem>
                        ))}
                    </CarouselSection>
                ) : (
                    <div className="md:px-12 md:py-8">
                        <DetailsPageRelatedTileGrid
                            type="projects"
                            title="Recently viewed"
                            titleClassName="!text-15 font-display font-semibold leading-[100%] text-[#3C3C3C] mb-2"
                            tiles={recentlyViewedProjectsAdmin}
                            displayCount={{
                                desktop: displayedRecentlyViewedProjects,
                                mobile: 4,
                            }}
                            noSlice={showingAllRecentlyViewedProjects}
                            appDisplayMode={desktop ? 'desktop' : 'mobile'}
                            cta={
                                showingAllRecentlyViewedProjects ||
                                recentlyViewedProjectsAdmin.length <=
                                    displayedRecentlyViewedProjects
                                    ? undefined
                                    : {
                                          type: 'link',
                                          to: `?${SHOW_ALL_PARAM_RECENTLY_VIEWED_PROJECTS}=1`,
                                          label: 'Show all Projects',
                                          preventScrollReset: true,
                                          loading:
                                              isLoadingRecentlyViewedProjects
                                                  ? true
                                                  : undefined,
                                          color: 'primary',
                                      }
                            }
                        />
                    </div>
                ))}

            {desktop && (
                <div className="md:px-12">
                    <DetailPageButtons
                        id={project.ID}
                        editLink={projectEditPath}
                        shareableLink={project.Link}
                        canEdit={canEditProjects}
                        canCopyLink={canCopyLink}
                        visibilityToggle={
                            isAdminUser && setInLibrary
                                ? {
                                      isInDirectory: optimisticInLibrary,
                                      itemType: 'project',
                                      onToggle: (inLibrary) => {
                                          setOptimisticInLibrary(inLibrary);
                                          setInLibrary(
                                              'project',
                                              project.ID,
                                              inLibrary,
                                          );
                                      },
                                  }
                                : undefined
                        }
                    />
                </div>
            )}
        </div>
    );
};

export default ProjectPageContent;
